<template>
	<div class="main-container">
		<div
			class="editor-container editor-container_classic-editor editor-container_include-style editor-container_include-block-toolbar"
			ref="editorContainerElement"
		>
			<div class="editor-container__editor">
				<div ref="editorElement" style="height: inherit;">
					<div v-if="isLoading" v-loading="isLoading" style="height: calc(100vh - 588px);"/>
					<ckeditor
						v-else-if="isLayoutReady"
						v-model="localData"
						:editor="editor"
						:config="coConfig"
						@ready="onEditorReady"
						@blur="onEditorBlur"
						ref="ckeditorInstance"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
	
<script>
import {
	ClassicEditor,
	AccessibilityHelp,
	Alignment,
	Autoformat,
	AutoImage,
	AutoLink,
	Autosave,
	BalloonToolbar,
	Base64UploadAdapter,
	BlockQuote,
	BlockToolbar,
	Bold,
	CloudServices,
	Code,
	CodeBlock,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	FullPage,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	ImageBlock,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	List,
	ListProperties,
	Paragraph,
	RemoveFormat,
	SelectAll,
	SourceEditing,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	// Table,
	// TableCaption,
	// TableCellProperties,
	// TableColumnResize,
	// TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo
} from 'ckeditor5';

import 'ckeditor5/dist/ckeditor5.css';
import Preview from './components/PreviewPlugin';
import Print from './components/PrintPlugin';
import FullScreen from './components/FullScreen';
import NormalScreen from './components/NormalScreen';

export default {
	name: 'app',
	props: {
		value: {
			type: String,
		},
		isShowFullScreenOption: {
			type: Boolean,
			default: false
		},
		isFullScreen: {
			type: Boolean,
			default: false
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		isHideToolbar: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isLayoutReady: false,
			config: {
				toolbar: {
					items:[
						'undo', 'redo', '|', 'fontFamily', 'fontSize', '|', 'bold', 'italic', 'underline', '|', 'link', '|',
						'bulletedList', 'numberedList', '|', 'code', '|', 'preview', 'print',
						'|', 'sourceEditing', '|', 'fontColor', 'fontBackgroundColor',
						'|', 'strikethrough', 'subscript', 'superscript', 'removeFormat', '|',
						'alignment', '|', 'indent', 'outdent', 
					],
					shouldNotGroupWhenFull: this.isShowFullScreenOption,
				},
				// removePlugins: ['Autoformat'],
				plugins: [
					FullScreen,
					NormalScreen,
					Preview,
					Print,
					AccessibilityHelp,
					Alignment,
					Autoformat,
					AutoImage,
					AutoLink,
					Autosave,
					BalloonToolbar,
					Base64UploadAdapter,
					BlockQuote,
					BlockToolbar,
					Bold,
					CloudServices,
					Code,
					CodeBlock,
					Essentials,
					FindAndReplace,
					FontBackgroundColor,
					FontColor,
					FontFamily,
					FontSize,
					FullPage,
					GeneralHtmlSupport,
					Heading,
					Highlight,
					HorizontalLine,
					ImageBlock,
					ImageInline,
					ImageInsert,
					ImageInsertViaUrl,
					ImageResize,
					ImageStyle,
					ImageTextAlternative,
					ImageToolbar,
					ImageUpload,
					Indent,
					IndentBlock,
					Italic,
					Link,
					List,
					ListProperties,
					Paragraph,
					RemoveFormat,
					SelectAll,
					SourceEditing,
					SpecialCharacters,
					SpecialCharactersArrows,
					SpecialCharactersCurrency,
					SpecialCharactersEssentials,
					SpecialCharactersLatin,
					SpecialCharactersMathematical,
					SpecialCharactersText,
					Strikethrough,
					Style,
					Subscript,
					Superscript,
					TableToolbar,
					TextTransformation,
					TodoList,
					Underline,
					Undo
				],
				balloonToolbar: ['bold', 'italic', '|', 'link', 'insertImage', '|', 'bulletedList', 'numberedList'],
				blockToolbar: [
					'fontSize',
					'fontColor',
					'fontBackgroundColor',
					'|',
					'bold',
					'italic',
					'|',
					'link',
					'insertImage',
					'insertTable',
					'|',
					'bulletedList',
					'numberedList',
					'outdent',
					'indent'
				],
				fontFamily: {
					supportAllValues: true
				},
				fontSize: {
					options: [6, 8, 10, 12, 14, 'default', 18, 20, 22, 24, 26, 28, 36, 48, 72],
					supportAllValues: true
				},
				heading: {
					options: [
						{
							model: 'paragraph',
							title: 'Paragraph',
							class: 'ck-heading_paragraph'
						},
						{
							model: 'heading1',
							view: 'h1',
							title: 'Heading 1',
							class: 'ck-heading_heading1'
						},
						{
							model: 'heading2',
							view: 'h2',
							title: 'Heading 2',
							class: 'ck-heading_heading2'
						},
						{
							model: 'heading3',
							view: 'h3',
							title: 'Heading 3',
							class: 'ck-heading_heading3'
						},
						{
							model: 'heading4',
							view: 'h4',
							title: 'Heading 4',
							class: 'ck-heading_heading4'
						},
						{
							model: 'heading5',
							view: 'h5',
							title: 'Heading 5',
							class: 'ck-heading_heading5'
						},
						{
							model: 'heading6',
							view: 'h6',
							title: 'Heading 6',
							class: 'ck-heading_heading6'
						}
					]
				},
				// fullPage: true,
				// extraAllowedContent: "code",
				// enterMode: 2,
				// allowedContent: true,
				htmlSupport: {
					allow: [
						{
							name: /.*/,
							styles: true,
							attributes: true,
							classes: true
						}
					]
				},
				image: {
					toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage']
				},
				link: {
					addTargetToExternalLinks: true,
					defaultProtocol: 'https://',
					decorators: {
						toggleDownloadable: {
							mode: 'manual',
							label: 'Downloadable',
							attributes: {
								download: 'file'
							}
						}
					}
				},
				list: {
					properties: {
						styles: true,
						startIndex: true,
						reversed: true
					}
				},
				style: {
					definitions: [
						{
							name: 'Article category',
							element: 'h3',
							classes: ['category']
						},
						{
							name: 'Title',
							element: 'h2',
							classes: ['document-title']
						},
						{
							name: 'Subtitle',
							element: 'h3',
							classes: ['document-subtitle']
						},
						{
							name: 'Info box',
							element: 'p',
							classes: ['info-box']
						},
						{
							name: 'Side quote',
							element: 'blockquote',
							classes: ['side-quote']
						},
						{
							name: 'Marker',
							element: 'span',
							classes: ['marker']
						},
						{
							name: 'Spoiler',
							element: 'span',
							classes: ['spoiler']
						},
						{
							name: 'Code (dark)',
							element: 'pre',
							classes: ['fancy-code', 'fancy-code-dark']
						},
						{
							name: 'Code (bright)',
							element: 'pre',
							classes: ['fancy-code', 'fancy-code-bright']
						}
					]
				},
				fontColor: {
					colors: [
					{
							color: '#000',
							label: 'Black'
						},
						{
							color: '#4d4d4d',
							label: 'Dim Grey'
						},
						{
							color: '#999',
							label: 'Grey'
						},
						{
							color: '#e6e5e5',
							label: 'Light Grey'
						},
						{
							color: '#fff',
							label: 'White'
						},
						{
							color: '#e54d4d',
							label: 'Red'
						},
						{
							color: '#e54d4d',
							label: 'Orange'
						},
						{
							color: '#e6e54d',
							label: 'Yellow'
						},
						{
							color: '#99e54d',
							label: 'Light Green'
						},
						{
							color: '#4de54d',
							label: 'Green'
						},
						{
							color: '#4ee599',
							label: 'Aquamarine'
						},
						{
							color: '#4ee5e6',
							label: 'Turquoise'
						},
						{
							color: '#4d99e5',
							label: 'Light Blue'
						},
						{
							color: '#4d4de5',
							label: 'Blue'
						},
						{
							color: '#994de6',
							label: 'Purple'
						}
					],
					colorPicker: {
						format: 'hex'
					}
				},
				fontBackgroundColor: {
					colors: [
					{
							color: '#000',
							label: 'Black'
						},
						{
							color: '#4d4d4d',
							label: 'Dim Grey'
						},
						{
							color: '#999',
							label: 'Grey'
						},
						{
							color: '#e6e5e5',
							label: 'Light Grey'
						},
						{
							color: '#fff',
							label: 'White'
						},
						{
							color: '#e54d4d',
							label: 'Red'
						},
						{
							color: '#e54d4d',
							label: 'Orange'
						},
						{
							color: '#e6e54d',
							label: 'Yellow'
						},
						{
							color: '#99e54d',
							label: 'Light Green'
						},
						{
							color: '#4de54d',
							label: 'Green'
						},
						{
							color: '#4ee599',
							label: 'Aquamarine'
						},
						{
							color: '#4ee5e6',
							label: 'Turquoise'
						},
						{
							color: '#4d99e5',
							label: 'Light Blue'
						},
						{
							color: '#4d4de5',
							label: 'Blue'
						},
						{
							color: '#994de6',
							label: 'Purple'
						}
					],
					colorPicker: {
						format: 'hex'
					}
				},
				table: {
					contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
				}
			},
			editor: ClassicEditor,
			localData: '',
			editorInstance: null,
		};
	},
	watch: {
		value(newVal) {
			if (newVal === '<p>&nbsp;</p>') {
				newVal = '';
			}
			this.localData = this.replaceClassWithInlineStyle(newVal);
			this.$emit('input', this.localData);
		},
		localData(newVal) {
			if (newVal === '<p>&nbsp;</p>') {
				newVal = '';
			}

			this.$emit('input', newVal);
		}
	},
	methods: {
		replaceClassWithInlineStyle(htmlString) {
			if (!htmlString) return '';

			// Replace if the content are normal text
			const hasHTMLTags = /<[^>]*>/.test(htmlString);
    		if (!hasHTMLTags) {
				htmlString = htmlString.replace(/\r\n|\r|\n/g, '<br/>');
			}

			// Preserve DOCTYPE if it exists
			let doctypeMatch = htmlString.match(/<!DOCTYPE [^>]+>/i);
			if (!doctypeMatch?.length > 0) {
				return htmlString;
			}
			
			let doctype = doctypeMatch ? doctypeMatch[0] + "\n" : "";

			// Parse the HTML string into a DOM object
			let parser = new DOMParser();
			let doc = parser.parseFromString(htmlString, 'text/html');
			let styles = {};

			// Extract styles from the <style> tag and remove it
			doc.querySelectorAll('style').forEach(styleTag => {
				let sheet = styleTag.sheet;
				if (sheet) {
					try {
						for (let rule of sheet.cssRules) {
							if (rule.selectorText) {
								let classMatches = rule.selectorText.match(/\.([\w-]+)/g);
								if (classMatches) {
									classMatches.forEach(cls => {
										let className = cls.replace('.', '');
										styles[className] = rule.style.cssText;
									});
								}
							}
						}
					} catch (e) {
						console.warn("Cannot access stylesheet rules: ", e);
					}
				}
				// Remove the <style> tag after extracting styles
				styleTag.remove();
			});

			// Replace class attributes with inline styles
			doc.querySelectorAll('[class]').forEach(element => {
				let classList = Array.from(element.classList);
				let inlineStyles = classList.map(cls => styles[cls] || '').join('; ');
				if (inlineStyles.trim()) {
					element.style.cssText += inlineStyles;
				}
				element.removeAttribute('class');
			});


			// Ensure <span> inside <i> does not override italic, some cases in win app add font-style: normal; to <span> inside <i>
			doc.querySelectorAll('span').forEach(element => {
				let computedStyle = element.getAttribute('style') || '';
				// Remove any "font-style: normal" from <span> inside <i>
				computedStyle = computedStyle.replace(/font-style:\s*normal;?/gi, '');
				element.setAttribute('style', computedStyle.trim());
			});

			// Serialize the modified DOM back to a string
			return doctype + doc.documentElement.outerHTML;
		},
		onEditorBlur() {
			if (this.localData === '<p>&nbsp;</p>') {
				this.localData = '';
				this.$emit('input', this.localData);
			}

			this.$emit('blur', this.localData); // Emit 'blur' event to parent
		},
		onEditorReady(editor) {
			try {
				this.editorInstance = editor;
				// this.editorInstance.setData(this.localData);
			} catch (error) {
				console.error('Error onEditorReady:', error);
			}
		},
		insertContent(content, isHtml = false) {
			this.editorInstance.model.change(writer => {
				const insertPosition = this.editorInstance.model.document.selection.getFirstPosition();
				writer.insertText( content, this.editorInstance.model.document.selection.getFirstPosition() );
			});
		}
	},
	mounted() {
		this.isLayoutReady = true;
		this.localData = this.value;
	},
	computed: {
		coConfig() {
			if (this.isHideToolbar) {
				this.config.toolbar = [];
				this.config.balloonToolbar = [];
			}

			let config = this.config;

			if (this.isFullScreen && this.isShowFullScreenOption) {
				config.toolbar.items.push('|');
				config.toolbar.items.push('normalScreen');
			} else if (this.isShowFullScreenOption) {
				config.toolbar.items.push('|');
				config.toolbar.items.push('fullScreen');
			}

			console.log('CKEditor Config:', config);
			return config;
		}
	}
};
</script>
